import { useMemo } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { AgeCategory } from '@mews-commander/data-access';
import { DateTimeService, DateTimeUtils, TimeService, useDateTimeLocalizationInfo, useTimezone } from '@mews/framework';
import { Interval } from '@mews-commander/types';
import { NewReservationFormKeys, NewReservationFormValues } from '../schema';
import { NewReservationPanelProps } from '../components/NewReservationPanel';

export interface Params extends Interval {
    resourceCategoryCapacity: number;
    defaultRateId: string | undefined;
    ageCategories: AgeCategory[];
    ratesOptions: NewReservationPanelProps['ratesOptions'];
}

export const useNewReservationFormDefaultValues = ({
    startUtc,
    endUtc,
    defaultRateId,
    resourceCategoryCapacity,
    ageCategories,
    ratesOptions,
}: Params): NewReservationFormValues => {
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();

    return useMemo(() => {
        const arrivalDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const departureTime = DateTimeService.fromUtcString(endUtc, timezone);

        const startHours = DateTimeUtils.dateTimeUtcStringToTime(startUtc, timezone);
        const endHours = DateTimeUtils.dateTimeUtcStringToTime(endUtc, timezone);

        return {
            [NewReservationFormKeys.ArrivalDate]: DateTimeService.toUtc(arrivalDateTime).value,
            [NewReservationFormKeys.DepartureDate]: DateTimeService.toUtc(departureTime).value,
            [NewReservationFormKeys.ArrivalHours]: TimeService.toShortString(startHours, localizationInfo),
            [NewReservationFormKeys.DepartureHours]: TimeService.toShortString(endHours, localizationInfo),
            [NewReservationFormKeys.Rate]: ratesOptions?.length ? defaultRateId : null,
            [NewReservationFormKeys.AgeCategories]: pipe(
                ageCategories,
                array.reduce({}, (acc, ageCategory) => ({
                    ...acc,
                    [ageCategory.id]: ageCategory.isDefault ? resourceCategoryCapacity : 0,
                }))
            ),
        };
    }, [
        startUtc,
        timezone,
        endUtc,
        localizationInfo,
        defaultRateId,
        ageCategories,
        resourceCategoryCapacity,
        ratesOptions,
    ]);
};
